import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/base/SEOHead';

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://personalinjury-junlawfirm.com';

const carAccidentSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/car-accidents#webpage`,
      url: `${siteUrl}/car-accidents`,
      name: 'Fishers Car Accident Lawyer | Indiana Auto Accident Attorney | Jun Law Firm',
      description:
        'Injured in a car accident in Indiana? Jun Law Firm fights for immigrant families to secure maximum compensation. Free case review. Call (317) 701-2768.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        '@id': `${siteUrl}/car-accidents#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Car Accident Attorney', item: `${siteUrl}/car-accidents` },
        ],
      },
    },
    {
      '@type': 'LegalService',
      '@id': `${siteUrl}/car-accidents#service`,
      name: 'Jun Law Firm - Car Accident Attorney Fishers Indiana',
      url: `${siteUrl}/car-accidents`,
      telephone: '+13177012768',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fishers',
        addressRegion: 'IN',
        postalCode: '46037',
        addressCountry: 'US',
      },
      areaServed: { '@type': 'State', name: 'Indiana' },
      description:
        'Experienced car accident attorney in Fishers, Indiana representing immigrant victims in rear-end collisions, truck accidents, motorcycle crashes, and rideshare accidents. No win, no fee.',
      priceRange: 'Free consultation',
      availableLanguage: [
        { '@type': 'Language', name: 'English' },
        { '@type': 'Language', name: 'Spanish' },
        { '@type': 'Language', name: 'Korean' },
        { '@type': 'Language', name: 'Chinese' },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Does my immigration status affect my right to sue after a car accident in Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. Under Indiana law, all car accident victims have the right to seek compensation regardless of immigration status. Insurance companies cannot deny your claim based on citizenship or visa status.',
          },
        },
        {
          '@type': 'Question',
          name: 'What should I do immediately after a car accident in Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Seek medical attention, report the accident to police, gather evidence at the scene, exchange information with the other driver, and contact a car accident attorney as soon as possible for a free case review.',
          },
        },
        {
          '@type': 'Question',
          name: 'What types of compensation can I recover after a car accident?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Car accident victims in Indiana may recover medical expenses, lost wages, pain and suffering, property damage, disability compensation, and loss of consortium.',
          },
        },
      ],
    },
  ],
};

export default function CarAccidents() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Add scroll animation
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('section');
    sections.forEach((section) => {
      section.classList.add('opacity-0');
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const accidentTypes = [
    {
      number: 'I',
      title: 'Rear-End Collisions',
      description: 'Most common type of accident, often caused by distracted driving or following too closely'
    },
    {
      number: 'II',
      title: 'Commercial Truck Accidents',
      description: 'Severe crashes involving semi-trucks, delivery vehicles, and large commercial vehicles'
    },
    {
      number: 'III',
      title: 'Motorcycle Crashes',
      description: 'High-risk accidents resulting in serious injuries due to lack of protection'
    },
    {
      number: 'IV',
      title: 'Rideshare Accidents',
      description: 'Uber and Lyft accidents with complex insurance coverage issues'
    },
    {
      number: 'V',
      title: 'T-Bone Collisions',
      description: 'Side-impact crashes at intersections causing severe injuries'
    },
    {
      number: 'VI',
      title: 'Head-On Crashes',
      description: 'Devastating high-speed collisions with catastrophic consequences'
    }
  ];

  const compensationTypes = [
    { label: 'Medical Expenses', detail: 'Emergency care, surgery, rehabilitation' },
    { label: 'Lost Wages', detail: 'Income loss during recovery' },
    { label: 'Pain & Suffering', detail: 'Physical and emotional trauma' },
    { label: 'Property Damage', detail: 'Vehicle repair or replacement' },
    { label: 'Disability', detail: 'Long-term or permanent impairment' },
    { label: 'Loss of Consortium', detail: 'Impact on family relationships' }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Free Consultation',
      description: 'We review your case details and explain your legal rights at no cost'
    },
    {
      number: '02',
      title: 'Investigation',
      description: 'We gather evidence, police reports, witness statements, and medical records'
    },
    {
      number: '03',
      title: 'Negotiation',
      description: 'We fight with insurance companies to secure maximum compensation'
    },
    {
      number: '04',
      title: 'Resolution',
      description: 'We obtain settlement or take your case to trial if necessary'
    }
  ];

  return (
    <div className="min-h-screen bg-lavender-50">
      <SEOHead
        title="Fishers Car Accident Lawyer | Indiana Auto Accident Attorney | Jun Law Firm"
        description="Injured in a car accident in Indiana? Jun Law Firm fights for immigrant families to secure maximum compensation. Free case review. Call (317) 701-2768. Multilingual — English, Spanish, Korean, Chinese."
        keywords="car accident attorney Fishers Indiana, auto accident lawyer Indiana, immigrant car accident attorney, truck accident lawyer, motorcycle crash attorney"
        canonicalPath="/car-accidents"
        schema={carAccidentSchema}
      />
      <Navbar />

      {/* Hero Section - Magazine Style */}
      <section className="relative pt-32 pb-20 bg-lavender-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="text-xs tracking-widest text-gray-500 mb-6 uppercase font-light" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Personal Injury Law
            </div>
            <h1 className="text-7xl md:text-8xl font-light text-gray-900 mb-8 leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>
              Car Accident <em className="font-light italic">Attorneys</em>
            </h1>
            <div className="w-24 h-px bg-gray-900 mb-8"></div>
            <p className="text-xl text-gray-700 mb-12 leading-relaxed max-w-2xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Injured in a car accident? Our experienced legal team fights for immigrant families across Indiana to secure the compensation you deserve. No win, no fee guarantee.
            </p>
            <div className="flex gap-6">
              <button onClick={scrollToContact} className="bg-gray-900 text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}>
                Get Free Case Review
              </button>
              <a href="tel:+13177012768" className="border-2 border-gray-900 text-gray-900 px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-900 hover:text-white transition-all cursor-pointer whitespace-nowrap flex items-center" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}>
                (317) 701-2768
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Accidents - Magazine Grid */}
      <section className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Our Expertise
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Types of <em className="italic">Car Accidents</em>
            </h2>
            <div className="w-16 h-px bg-gray-900"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
            {accidentTypes.map((type, index) => (
              <article key={index} className="group cursor-pointer">
                <div className="text-6xl font-light text-gray-300 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {type.number}
                </div>
                <h3 className="text-2xl font-light text-gray-900 mb-3 group-hover:text-gray-600 transition-colors" style={{ fontFamily: "'Crimson Pro', serif" }}>
                  {type.title}
                </h3>
                <div className="w-12 h-px bg-gray-300 mb-4"></div>
                <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {type.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Compensation Section */}
      <section className="py-24 bg-lavender-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
                Your Rights
              </div>
              <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Compensation You <em className="italic">Deserve</em>
              </h2>
              <div className="w-16 h-px bg-gray-900 mb-8"></div>
              <p className="text-lg text-gray-700 leading-relaxed" style={{ fontFamily: "'Crimson Pro', serif" }}>
                Car accident victims are entitled to full compensation for all damages. We fight to recover every dollar you're owed under Indiana law.
              </p>
            </div>
            <div className="space-y-8">
              {compensationTypes.map((comp, index) => (
                <article key={index} className="border-l-2 border-gray-900 pl-6 hover:border-gray-500 transition-colors">
                  <div className="text-xs tracking-widest text-gray-500 mb-2 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  <h3 className="text-xl font-light text-gray-900 mb-2" style={{ fontFamily: "'Crimson Pro', serif" }}>
                    {comp.label}
                  </h3>
                  <p className="text-sm text-gray-600" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {comp.detail}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 bg-gray-900 text-white border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="text-xs tracking-widest text-gray-400 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              How We Work
            </div>
            <h2 className="text-5xl md:text-6xl font-light mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Our <em className="italic">Legal Process</em>
            </h2>
            <div className="w-16 h-px bg-white mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
            {processSteps.map((step, index) => (
              <article key={index} className="text-center">
                <div className="text-7xl font-light text-gray-700 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {step.number}
                </div>
                <h3 className="text-xl font-light mb-4" style={{ fontFamily: "'Crimson Pro', serif" }}>
                  {step.title}
                </h3>
                <div className="w-12 h-px bg-gray-700 mx-auto mb-4"></div>
                <p className="text-sm text-gray-400 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {step.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Immigrant Rights Section */}
      <section className="py-24 bg-lavender-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Your Rights Protected
            </div>
            <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Your Immigration Status <em className="italic">Does Not Matter</em>
            </h2>
            <div className="w-16 h-px bg-gray-900 mx-auto mb-8"></div>
          </div>
          <p className="text-lg text-gray-700 mb-12 leading-relaxed text-center" style={{ fontFamily: "'Crimson Pro', serif" }}>
            Under Indiana law, all car accident victims have the right to seek compensation regardless of immigration status. Insurance companies cannot deny your claim based on citizenship or visa status. We protect your privacy and fight for your rights.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                01
              </div>
              <h3 className="text-lg font-light text-gray-900 mb-2" style={{ fontFamily: "'Crimson Pro', serif" }}>
                Multilingual Support
              </h3>
              <div className="w-8 h-px bg-gray-300 mx-auto"></div>
            </div>
            <div className="text-center">
              <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                02
              </div>
              <h3 className="text-lg font-light text-gray-900 mb-2" style={{ fontFamily: "'Crimson Pro', serif" }}>
                Confidential Consultation
              </h3>
              <div className="w-8 h-px bg-gray-300 mx-auto"></div>
            </div>
            <div className="text-center">
              <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                03
              </div>
              <h3 className="text-lg font-light text-gray-900 mb-2" style={{ fontFamily: "'Crimson Pro', serif" }}>
                Protected Rights
              </h3>
              <div className="w-8 h-px bg-gray-300 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Contact Us
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Get Your <em className="italic">Free Case Review</em>
            </h2>
            <div className="w-16 h-px bg-gray-900 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Available 24/7 — No obligation consultation
            </p>
          </div>
          <div className="bg-white border border-gray-200 p-12">
            <form className="space-y-8" data-readdy-form id="car-accident-contact">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-xs tracking-wider text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}>Full Name *</label>
                  <input 
                    type="text" 
                    name="name"
                    required
                    className="w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-gray-900 focus:ring-0 outline-none transition-all text-base bg-transparent"
                    style={{ fontFamily: "'Crimson Pro', serif" }}
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-wider text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}>Phone Number *</label>
                  <input 
                    type="tel" 
                    name="phone"
                    required
                    className="w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-gray-900 focus:ring-0 outline-none transition-all text-base bg-transparent"
                    style={{ fontFamily: "'Crimson Pro', serif" }}
                    placeholder="(317) 555-0123"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs tracking-wider text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}>Email Address *</label>
                <input 
                  type="email" 
                  name="email"
                  required
                  className="w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-gray-900 focus:ring-0 outline-none transition-all text-base bg-transparent"
                  style={{ fontFamily: "'Crimson Pro', serif" }}
                  placeholder="your.email@example.com"
                />
              </div>
              <div>
                <label className="block text-xs tracking-wider text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}>Accident Date</label>
                <input 
                  type="date" 
                  name="accident_date"
                  className="w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-gray-900 focus:ring-0 outline-none transition-all text-base bg-transparent"
                  style={{ fontFamily: "'Crimson Pro', serif" }}
                />
              </div>
              <div>
                <label className="block text-xs tracking-wider text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}>Describe Your Accident *</label>
                <textarea 
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-0 py-3 border-0 border-b border-gray-300 focus:border-gray-900 focus:ring-0 outline-none transition-all resize-none text-base bg-transparent"
                  style={{ fontFamily: "'Crimson Pro', serif" }}
                  placeholder="Please describe what happened, injuries sustained, and any other relevant details... (Max 500 characters)"
                ></textarea>
              </div>
              <button 
                type="submit"
                className="w-full bg-gray-900 text-white py-5 text-sm tracking-widest uppercase hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}
              >
                Submit Free Case Review
              </button>
              <p className="text-xs text-gray-500 text-center leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                By submitting this form, you agree to be contacted by our legal team. Your information is confidential and protected.
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
